/**
 * Build otpauth:// URIs (Google Authenticator style) for QR import.
 * Spec: https://github.com/google/google-authenticator/wiki/Key-Uri-Format
 *
 * Every field is required: TOTP defaults live in `totp.ts` alone, so this module
 * cannot drift from the values the app actually generates codes with.
 */

import { algorithmLabel, type HashAlgorithm } from "./totp.ts";

export type OtpAuthParams = {
  secret: string;
  /** Shown as issuer prefix in authenticator apps. Empty string to omit. */
  issuer: string;
  /** Account name / label (e.g. email). Empty string to omit. */
  account: string;
  digits: number;
  period: number;
  algorithm: HashAlgorithm;
};

/**
 * Build an `otpauth://totp/...` URI suitable for QR codes.
 * Spaces in secret are stripped; other params validated lightly.
 */
export function buildOtpAuthUri(params: OtpAuthParams): string {
  const secret = params.secret.replaceAll(/\s+/g, "").toUpperCase();
  if (secret.length === 0) {
    throw new Error("Secret is required");
  }

  const issuer = params.issuer.trim();
  const account = params.account.trim();

  // The label is a path segment, so it needs percent-encoding throughout —
  // `+` would be read as a literal plus rather than a space.
  const label =
    issuer && account
      ? `${encodeURIComponent(issuer)}:${encodeURIComponent(account)}`
      : encodeURIComponent(account || issuer) || "TOTP";

  const query = new URLSearchParams();
  query.set("secret", secret);
  if (issuer) {
    query.set("issuer", issuer);
  }
  query.set("algorithm", algorithmLabel(params.algorithm));
  query.set("digits", String(params.digits));
  query.set("period", String(params.period));

  return `otpauth://totp/${label}?${query.toString()}`;
}
